import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import WizardFrame from './WizardFrame';
import ConnectionAvatar from './ConnectionAvatar';
import { INTEGRATIONS_LOGO_PATH } from '../common/constants';
import {
  getMyobFiles,
  getMyobFileJobs,
  saveMyobFileMapping,
  startOAuth
} from '../../utils/api_integrations';

// ---------------------------------------------------------------- #
// MyobWizard
// ---------------------------------------------------------------- #
//
// The MYOB wizard (plan 06-myob.md):
//   1. Connect: the entity's company files with their parks,
//      "Connect a MYOB company file", and per file "Map parks" (which
//      opens step 2 on that file).
//   2. Map parks: the file being mapped (a dropdown to switch when there
//      are several), "Importing jobs from MYOB" until its jobs have been
//      imported, then one row per park: linked, method (whole company file
//      or jobs), jobs, unallocated. The mapping rules are checked as you go.
//   3. Review: what will change, any rule problems, and Save (the only
//      step that writes). Save sends the whole file: a park left unticked
//      is unlinked from it.
//
// There is no "disconnect at MYOB": unticking every park on a file stops
// the ETL reading it, as WordPress did.
//
// Props (the same contract as every provider wizard):
//   register       The `data` of GET register
//   parkiId        The park the wizard was opened from, or null
//   initialStep    The step to open at (2, Map parks, after returning from MYOB)
//   connectionKey  The myobId to open on, or null
//   canConnect     False while staff are viewing as the customer
//   onClose        Called with true when something was saved, false otherwise

// ---------------------------------------------------------------- #
// Constants
// ---------------------------------------------------------------- #

/** The three step labels. */
const MYOB_STEPS = ['Connect', 'Map parks', 'Review'];

/** Step numbers, named so the code reads plainly. */
const STEP_CONNECT = 1;
const STEP_MAP = 2;
const STEP_REVIEW = 3;

/** Gap in pixels between a locked pill and the tooltip's caret. */
const PILL_TOOLTIP_GAP = 8;

/** The two stored mapping methods ("trackingCategory" is what MYOB jobs are stored as). */
const METHOD_WHOLE = 'wholeTenancy';
const METHOD_JOBS = 'trackingCategory';

/** What the user sees for each mapping method. */
const METHOD_LABELS = {
  [METHOD_WHOLE]: 'Whole company file',
  [METHOD_JOBS]: 'Jobs'
};

/** Message shown on the Connect button while staff are viewing as the customer. */
const IMPERSONATING_MESSAGE = 'Ask the customer to connect their own account';

/** Shown while the Batch import has not yet filled the file's jobs. */
const IMPORTING_MESSAGE = 'Importing jobs from MYOB. This usually takes a few minutes; you can leave this page.';

/** Shown for a file no park has been saved to yet: its jobs are only imported once one is. */
const NOT_IMPORTED_MESSAGE = 'MYOB has not sent this file\'s jobs yet. Parki imports them when the first park is saved to this file, which takes a few minutes. To split by job, link one park to the whole company file, save, then come back to pick its jobs.';

/** How often to check again while the import is running, in milliseconds (30 seconds). */
const IMPORT_POLL_INTERVAL_MS = 30000;

/** Label for a park whose register rows Parki support has to sort out. */
const CONTACT_SUPPORT_LABEL = 'Contact support';

/** Label for a park whose plan does not include bookkeeping. */
const NOT_IN_PLAN_LABEL = 'Not in plan';

/** Reason shown for a linked park that is not in the admin's park list; its link is kept as it is. */
const OUTSIDE_PLAN_REASON = 'This park is not in your current park list, so its link is kept as it is.';

/** What the save result's batchJob value means to the user. */
const BATCH_JOB_MESSAGES = {
  submitted: 'Parki has started importing this company file. Figures appear once the import finishes.',
  skipped: 'An import of this company file is already running.',
  logged: 'Test mode, so the import was logged instead of started.',
  failed: 'Your mapping is saved, but the import could not be started. Please contact Parki support so they can start it.'
};

// ---------------------------------------------------------------- #
// Helpers: choices
// ---------------------------------------------------------------- #

/**
 * Leave out files MYOB no longer accepts and that have no parks: MYOB has not accepted their sign-in for over a week
 * (tokenIsStale from the API). A newly connected file has a fresh token, so it stays even before a park is saved.
 * A stale file that still has parks stays too, and the register shows those parks as needing a reconnect.
 */
const keepConnectedFiles = (files) => {
  const kept = [];
  for (const file of files) {
    const isGone = file.tokenIsStale === true && !fileHasSavedParks(file);
    if (isGone) {
      continue;
    }
    kept.push(file);
  }
  return kept;
};

/**
 * True when the file has a park saved to it, ours or another entity's. MYOB jobs are only imported once it has.
 */
const fileHasSavedParks = (file) => {
  if (!file) {
    return false;
  }
  const ownParkCount = (file.parks || []).length;
  const otherParkCount = file.otherParkCount || 0;
  return ownParkCount + otherParkCount > 0;
};

/**
 * A park's choice when it is not linked to the file.
 */
const buildEmptyChoice = () => {
  return {
    linked: false,
    integrationMethod: METHOD_WHOLE,
    jobs: [],
    unallocatedTransactions: false
  };
};

/**
 * Turn a list of ids into strings, so comparisons do not trip over numbers.
 */
const toStringList = (values) => {
  const strings = [];
  for (const value of values || []) {
    strings.push(String(value));
  }
  return strings;
};

/**
 * True when two id lists hold the same ids, in any order.
 */
const sameIdList = (first, second) => {
  // Lists of different lengths cannot match
  if (first.length !== second.length) {
    return false;
  }

  // Every id in the first list must be in the second
  for (const id of first) {
    if (!second.includes(id)) {
      return false;
    }
  }
  return true;
};

/**
 * True when two park choices would save the same thing.
 */
const choicesMatch = (first, second) => {
  // Linked or not decides first
  if (first.linked !== second.linked) {
    return false;
  }

  // Two unlinked parks are the same whatever their other fields say
  if (!first.linked) {
    return true;
  }

  // Compare every saved field
  const sameMethod = first.integrationMethod === second.integrationMethod;
  const sameJobs = sameIdList(first.jobs, second.jobs);
  const sameUnallocated = first.unallocatedTransactions === second.unallocatedTransactions;
  return sameMethod && sameJobs && sameUnallocated;
};

/**
 * Find one company file in the list by its myobId, or null.
 */
const findFile = (files, myobId) => {
  if (!myobId) {
    return null;
  }
  for (const file of files || []) {
    if (String(file.myobId) === String(myobId)) {
      return file;
    }
  }
  return null;
};

/**
 * Pick the file to open on: the one asked for, else the one the park uses, else the only one.
 */
const pickInitialFileId = (files, connectionKey, parkiId) => {
  // The file the page asked for (a warning's Fix, or a return from MYOB)
  const askedFor = findFile(files, connectionKey);
  if (askedFor) {
    return String(askedFor.myobId);
  }

  // The first file the opened park is linked to
  const parkGiven = parkiId !== null && parkiId !== undefined;
  if (parkGiven) {
    for (const file of files) {
      for (const park of file.parks || []) {
        if (String(park.parkiId) === String(parkiId)) {
          return String(file.myobId);
        }
      }
    }
  }

  // With only one file there is nothing to choose
  if (files.length === 1) {
    return String(files[0].myobId);
  }
  return null;
};

/**
 * Build the rows of the map table: every park in the register, plus any park linked to this file that is not.
 */
const buildRows = (register, file) => {
  const rows = [];
  const seenParkKeys = {};

  // One row per park in the register
  for (const park of register?.parks || []) {
    const parkKey = String(park.parkiId);
    const cell = park.cells?.bookkeeping || {};

    // Note the Xero organisations this park already uses (one bookkeeping provider per park)
    const xeroNames = [];
    for (const link of cell.mapping?.links || []) {
      if (link.provider === 'xero') {
        xeroNames.push(link.connectionName);
      }
    }

    // Parks the register will not let us change are shown but locked
    let locked = false;
    let lockedReason = null;
    if (cell.editable !== true) {
      locked = true;
      lockedReason = NOT_IN_PLAN_LABEL;
      if (cell.state === 'contact-support') {
        lockedReason = CONTACT_SUPPORT_LABEL;
      }
      if (cell.reason) {
        lockedReason = `${lockedReason}: ${cell.reason}`;
      }
    }

    rows.push({
      parkiId: park.parkiId,
      parkKey: parkKey,
      parkName: park.parkName,
      cell: cell,
      locked: locked,
      lockedReason: lockedReason,
      xeroNames: xeroNames
    });
    seenParkKeys[parkKey] = true;
  }

  // A park linked here but missing from the register keeps its link, locked, so a save does not unlink it
  for (const filePark of file?.parks || []) {
    const parkKey = String(filePark.parkiId);
    if (seenParkKeys[parkKey]) {
      continue;
    }
    rows.push({
      parkiId: filePark.parkiId,
      parkKey: parkKey,
      parkName: filePark.parkName,
      cell: {},
      locked: true,
      lockedReason: OUTSIDE_PLAN_REASON,
      xeroNames: []
    });
  }

  return rows;
};

/**
 * Read each park's stored settings on one file into the choices the form edits.
 */
const buildChoicesForFile = (rows, file) => {
  // Every row starts unlinked
  const choices = {};
  for (const row of rows) {
    choices[row.parkKey] = buildEmptyChoice();
  }

  // Fill in the parks the file already links
  for (const filePark of file?.parks || []) {
    const parkKey = String(filePark.parkiId);
    let method = METHOD_JOBS;
    if (filePark.integrationMethod === METHOD_WHOLE) {
      method = METHOD_WHOLE;
    }
    choices[parkKey] = {
      linked: true,
      integrationMethod: method,
      jobs: toStringList(filePark.jobs),
      unallocatedTransactions: filePark.unallocatedTransactions === true
    };
  }

  return choices;
};

/**
 * The method a newly ticked park starts with: whole company file when it is alone, otherwise jobs.
 */
const pickDefaultMethod = (choices, parkKey) => {
  // Look for any other park already ticked
  for (const otherKey of Object.keys(choices)) {
    const otherIsLinked = otherKey !== parkKey && choices[otherKey].linked;
    if (otherIsLinked) {
      return METHOD_JOBS;
    }
  }
  return METHOD_WHOLE;
};

// ---------------------------------------------------------------- #
// Helpers: jobs
// ---------------------------------------------------------------- #

/**
 * The names of some jobs, joined with commas.
 */
const describeJobs = (jobList, jobIds) => {
  // Look each id up in the imported jobs
  const names = [];
  for (const jobId of jobIds) {
    let name = `${jobId} (no longer in MYOB)`;
    for (const job of jobList?.jobs || []) {
      if (String(job.jobId) === jobId) {
        name = job.jobName;
      }
    }
    names.push(name);
  }
  return names.join(', ');
};

/**
 * Describe one linked park's settings as one line for the review list.
 */
const describeSettings = (choice, jobList) => {
  const parts = [];

  // Whole company file needs no more words
  if (choice.integrationMethod === METHOD_WHOLE) {
    parts.push('Whole company file');
    return parts.join('. ');
  }

  // By job: the job names, when any are picked
  const hasJobs = choice.jobs.length > 0;
  if (hasJobs) {
    parts.push(`Jobs: ${describeJobs(jobList, choice.jobs)}`);
  }

  // The unallocated flag: on its own it is the only thing the park gets
  if (choice.unallocatedTransactions && hasJobs) {
    parts.push('Also gets transactions with no job');
  }
  if (choice.unallocatedTransactions && !hasJobs) {
    parts.push('Only transactions with no job');
  }

  // Neither: say so, so the review shows what is missing
  if (!choice.unallocatedTransactions && !hasJobs) {
    parts.push('By job, no jobs picked');
  }

  return parts.join('. ');
};

// ---------------------------------------------------------------- #
// Helpers: the mapping rules
// ---------------------------------------------------------------- #

/**
 * Check the whole file against the mapping rules; returns one problem per broken rule per park.
 */
const checkMappingRules = (rows, choices, jobList, fileName) => {
  const problems = [];

  // Collect the rows that are linked
  const linkedRows = [];
  for (const row of rows) {
    const choice = choices[row.parkKey];
    if (!choice || !choice.linked) {
      continue;
    }
    linkedRows.push(row);
  }
  const moreThanOnePark = linkedRows.length > 1;

  // Rule: a whole-company-file park is the only park on the file
  for (const row of linkedRows) {
    const isWhole = choices[row.parkKey].integrationMethod === METHOD_WHOLE;
    if (isWhole && moreThanOnePark) {
      problems.push({
        parkiId: row.parkiId,
        message: `${row.parkName} is set to whole company file, so it must be the only park on ${fileName}. Use jobs, or unlink the other parks.`
      });
    }
  }

  // Collect the parks that receive unallocated transactions
  const unallocatedRows = [];
  for (const row of linkedRows) {
    if (choices[row.parkKey].unallocatedTransactions) {
      unallocatedRows.push(row);
    }
  }

  // Rule: at most one park receives unallocated transactions
  if (unallocatedRows.length > 1) {
    const unallocatedNames = [];
    for (const row of unallocatedRows) {
      unallocatedNames.push(row.parkName);
    }
    for (const row of unallocatedRows) {
      problems.push({
        parkiId: row.parkiId,
        message: `Only one park on ${fileName} can receive unallocated transactions, but ${unallocatedNames.join(' and ')} are all ticked.`
      });
    }
  }

  // Rule: only a jobs park can receive unallocated transactions
  for (const row of unallocatedRows) {
    const isWhole = choices[row.parkKey].integrationMethod === METHOD_WHOLE;
    if (isWhole) {
      problems.push({
        parkiId: row.parkiId,
        message: `${row.parkName} uses the whole company file, so it cannot also receive unallocated transactions. Only a jobs park can.`
      });
    }
  }

  // Only check ids against MYOB's list once the jobs have been imported
  const jobsImported = Boolean(jobList) && jobList.jobsImported === true;
  const fileJobIds = [];
  for (const job of jobList?.jobs || []) {
    fileJobIds.push(String(job.jobId));
  }

  // Rule: a jobs park takes something from this file: at least one job, or the transactions with no job
  for (const row of linkedRows) {
    const choice = choices[row.parkKey];
    if (choice.integrationMethod !== METHOD_JOBS) {
      continue;
    }

    // A job, or the "transactions with no job" tick, must be picked
    const hasJobs = choice.jobs.length > 0;
    const takesNoJobTransactions = choice.unallocatedTransactions === true;
    if (!hasJobs && !takesNoJobTransactions) {
      problems.push({ parkiId: row.parkiId, message: `${row.parkName}: pick at least one job, or tick "Also gets transactions with no job".` });
      continue;
    }

    // Every job must still be in the file
    if (!jobsImported) {
      continue;
    }
    for (const jobId of choice.jobs) {
      if (!fileJobIds.includes(jobId)) {
        problems.push({ parkiId: row.parkiId, message: `${row.parkName}: one of its jobs is no longer in ${fileName}. Pick again.` });
        break;
      }
    }
  }

  // Rule: one bookkeeping provider per park
  for (const row of linkedRows) {
    if (row.xeroNames.length > 0) {
      problems.push({ parkiId: row.parkiId, message: `${row.parkName} is already set up with Xero. Remove it from its Xero organisations first.` });
    }
  }

  return problems;
};

// ---------------------------------------------------------------- #
// The wizard
// ---------------------------------------------------------------- #

/**
 * The four-step MYOB wizard.
 */
const MyobWizard = ({ register, parkiId = null, initialStep = 1, connectionKey = null, canConnect = true, onClose }) => {

  // ---------------------------------------------------------------- #
  // State
  // ---------------------------------------------------------------- #

  const [step, setStep] = useState(initialStep);
  const [maxReachedStep, setMaxReachedStep] = useState(initialStep);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fileId, setFileId] = useState(null);
  const [jobList, setJobList] = useState(null);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [jobsReloadCount, setJobsReloadCount] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [changed, setChanged] = useState(false);
  const [savedChoices, setSavedChoices] = useState({});
  const [choices, setChoices] = useState({});
  const [apiProblems, setApiProblems] = useState([]);
  const [saveSummary, setSaveSummary] = useState(null);
  const [parkFilter, setParkFilter] = useState('');
  const [activeParkKey, setActiveParkKey] = useState(null);

  // The tooltip over a locked pill, naming the park that uses it, or null when hidden
  const [pillTip, setPillTip] = useState(null);

  // The opened park is ticked for the first file only, so it is not ticked again after a save
  const preselectDone = useRef(false);

  // The opened park's row, so the Map parks step can scroll to it
  const openedParkRow = useRef(null);

  // The file being mapped, and the rows of its map table
  const selectedFile = findFile(files, fileId);
  const fileName = selectedFile?.companyName || 'this company file';
  const rows = buildRows(register, selectedFile);

  // ---------------------------------------------------------------- #
  // Choosing the file
  // ---------------------------------------------------------------- #

  /**
   * Make a file the one being mapped, and read its parks into the form.
   */
  const applyFile = (newFileId, fileList) => {
    // Read the stored settings into the form
    const file = findFile(fileList, newFileId);
    const fileRows = buildRows(register, file);
    const storedChoices = buildChoicesForFile(fileRows, file);
    const formChoices = { ...storedChoices };

    // Tick the opened park when it has no bookkeeping link anywhere yet
    const shouldPreselect = Boolean(file) && parkiId !== null && !preselectDone.current;
    if (shouldPreselect) {
      preselectDone.current = true;
      for (const row of fileRows) {
        const isOpenedPark = String(row.parkiId) === String(parkiId);
        const existingLinks = row.cell.mapping?.links || [];
        const canPreselect = isOpenedPark && !row.locked && existingLinks.length === 0;
        if (!canPreselect) {
          continue;
        }
        const method = pickDefaultMethod(storedChoices, row.parkKey);
        formChoices[row.parkKey] = { ...buildEmptyChoice(), linked: true, integrationMethod: method };
      }
    }

    // Show the file, and clear what belonged to the last one
    setFileId(newFileId);
    setSavedChoices(storedChoices);
    setChoices(formChoices);
    setJobList(null);
    setApiProblems([]);
    setSaveSummary(null);
  };

  // ---------------------------------------------------------------- #
  // Load the company files
  // ---------------------------------------------------------------- #

  /**
   * Read the entity's company files again; returns the list.
   */
  const loadFiles = async () => {
    const result = await getMyobFiles();
    const fileList = keepConnectedFiles(result.data.files || []);
    setFiles(fileList);
    return fileList;
  };

  // Read the files when the wizard opens, and pick the one to map
  useEffect(() => {
    let cancelled = false;
    const loadOnOpen = async () => {
      try {
        const result = await getMyobFiles();
        if (cancelled) {
          return;
        }
        const fileList = keepConnectedFiles(result.data.files || []);
        setFiles(fileList);

        // Open on the file the page asked for, or the obvious one
        const initialFileId = pickInitialFileId(fileList, connectionKey, parkiId);
        if (initialFileId) {
          applyFile(initialFileId, fileList);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Your MYOB company files could not be loaded.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    loadOnOpen();

    return () => {
      cancelled = true;
    };
    // Runs once on open; the props it reads do not change while the wizard is open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------------------------------------------------------------- #
  // Load the jobs
  // ---------------------------------------------------------------- #

  // Read the chosen file's jobs, and again on every "check again"
  useEffect(() => {
    if (!fileId) {
      return;
    }

    let cancelled = false;
    const loadJobs = async () => {
      setJobsLoading(true);
      try {
        const result = await getMyobFileJobs(fileId);
        if (!cancelled) {
          setJobList(result.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'The jobs could not be loaded.');
        }
      } finally {
        if (!cancelled) {
          setJobsLoading(false);
        }
      }
    };
    loadJobs();

    return () => {
      cancelled = true;
    };
  }, [fileId, jobsReloadCount]);

  // While an import is really running (the file has parks, so it was started), check again every 30 seconds
  const fileHasParks = fileHasSavedParks(selectedFile);
  useEffect(() => {
    const jobsMissing = Boolean(jobList) && jobList.jobsImported !== true;
    const stillImporting = jobsMissing && fileHasParks;
    if (!stillImporting) {
      return;
    }
    const timer = setTimeout(() => {
      setJobsReloadCount(previous => previous + 1);
    }, IMPORT_POLL_INTERVAL_MS);
    return () => clearTimeout(timer);
  }, [jobList, fileHasParks]);

  // ---------------------------------------------------------------- #
  // Step navigation
  // ---------------------------------------------------------------- #

  /**
   * Show a step and remember it as reached.
   */
  const goToStep = (stepNumber) => {
    setError(null);
    setStep(stepNumber);
    if (stepNumber > maxReachedStep) {
      setMaxReachedStep(stepNumber);
    }
  };

  // Hide the pill tooltip when anything scrolls, so it never floats away from its pill
  useEffect(() => {
    if (!pillTip) {
      return;
    }
    const hideOnScroll = () => {
      setPillTip(null);
    };
    window.addEventListener('scroll', hideOnScroll, true);
    return () => {
      window.removeEventListener('scroll', hideOnScroll, true);
    };
  }, [pillTip]);

  // Bring the opened park's row into view when the map step opens
  useEffect(() => {
    const onMapStep = step === STEP_MAP;
    if (onMapStep && openedParkRow.current) {
      openedParkRow.current.scrollIntoView({ block: 'center' });
    }
  }, [step]);

  // ---------------------------------------------------------------- #
  // Changes and the rules
  // ---------------------------------------------------------------- #

  /**
   * List every park whose choice differs from what is saved.
   */
  const buildChanges = () => {
    const changes = [];
    for (const row of rows) {
      const before = savedChoices[row.parkKey] || buildEmptyChoice();
      const after = choices[row.parkKey] || buildEmptyChoice();
      if (choicesMatch(before, after)) {
        continue;
      }

      // Say what kind of change it is
      let kind = 'changed';
      if (!before.linked) {
        kind = 'linked';
      } else if (!after.linked) {
        kind = 'unlinked';
      }
      changes.push({ row: row, before: before, after: after, kind: kind });
    }
    return changes;
  };

  /**
   * True when there are choices that have not been saved.
   */
  function isDirty() {
    return buildChanges().length > 0;
  }

  // The rule problems with the form as it stands
  const ruleProblems = checkMappingRules(rows, choices, jobList, fileName);

  /**
   * The rule problems for one park, as a list of messages.
   */
  const problemsForPark = (rowParkiId) => {
    const messages = [];
    for (const problem of ruleProblems) {
      if (String(problem.parkiId) === String(rowParkiId)) {
        messages.push(problem.message);
      }
    }
    return messages;
  };

  // ---------------------------------------------------------------- #
  // Step 1: connect
  // ---------------------------------------------------------------- #

  /**
   * Send the browser to MYOB's sign-in.
   */
  const handleConnect = async () => {
    // Leaving the app loses unsaved choices, so ask first
    if (isDirty()) {
      const userAgreed = window.confirm('Discard changes?');
      if (!userAgreed) {
        return;
      }
    }

    setBusy(true);
    setError(null);
    try {
      // Ask the API for the sign-in address and go there
      const result = await startOAuth('myob', parkiId);
      window.location.href = result.data.authoriseUrl;
    } catch (err) {
      setError(err.message || 'MYOB sign-in could not be started.');
      setBusy(false);
    }
  };

  /**
   * Jump from step 1 straight to mapping one file.
   */
  const handleMapFile = (file) => {
    const newFileId = String(file.myobId);
    const isDifferent = newFileId !== String(fileId);

    // Switching file throws away unsaved choices, so ask first
    if (isDifferent && isDirty()) {
      const userAgreed = window.confirm('Discard changes?');
      if (!userAgreed) {
        return;
      }
    }

    // Read it into the form and go straight to mapping its parks
    if (isDifferent) {
      applyFile(newFileId, files);
    }
    goToStep(STEP_MAP);
  };

  // ---------------------------------------------------------------- #
  // Step 2: switch file
  // ---------------------------------------------------------------- #

  /**
   * Switch the Map parks step to a different file.
   */
  const handlePickFile = (newFileId) => {
    // Nothing to do when it is already picked
    if (newFileId === String(fileId)) {
      return;
    }

    // Switching throws away unsaved choices, so ask first
    if (isDirty()) {
      const userAgreed = window.confirm('Discard changes?');
      if (!userAgreed) {
        return;
      }
    }
    applyFile(newFileId, files);
  };

  /**
   * Check again whether the import has finished.
   */
  const handleCheckImport = () => {
    setJobsReloadCount(previous => previous + 1);
  };

  // ---------------------------------------------------------------- #
  // Step 2: edit the choices
  // ---------------------------------------------------------------- #

  /**
   * Replace some fields of one park's choice.
   */
  const updateChoice = (parkKey, fields) => {
    // A new edit means the last save result no longer describes the screen
    setSaveSummary(null);
    setApiProblems([]);

    setChoices(previous => {
      const parkChoice = { ...(previous[parkKey] || buildEmptyChoice()), ...fields };
      return { ...previous, [parkKey]: parkChoice };
    });
  };

  /**
   * Tick or untick a park as linked to this file.
   */
  const handleLinkedChange = (parkKey, linked) => {
    // Unlinking clears the park's settings
    if (!linked) {
      updateChoice(parkKey, buildEmptyChoice());
      return;
    }

    // Linking starts with the method that fits how many parks are ticked
    const method = pickDefaultMethod(choices, parkKey);
    updateChoice(parkKey, { ...buildEmptyChoice(), linked: true, integrationMethod: method });
  };

  /**
   * Change a park's method; whole company file drops the jobs and the unallocated flag.
   */
  const handleMethodChange = (parkKey, method) => {
    if (method === METHOD_WHOLE) {
      updateChoice(parkKey, {
        integrationMethod: METHOD_WHOLE,
        jobs: [],
        unallocatedTransactions: false
      });
      return;
    }
    updateChoice(parkKey, { integrationMethod: METHOD_JOBS });
  };

  /**
   * Tick or untick one job for a park.
   */
  const handleJobTick = (parkKey, jobId, ticked) => {
    // Start from the current list without this job
    const currentJobs = choices[parkKey]?.jobs || [];
    const newJobs = [];
    for (const existingId of currentJobs) {
      if (existingId !== jobId) {
        newJobs.push(existingId);
      }
    }

    // Add the job back when it is ticked
    if (ticked) {
      newJobs.push(jobId);
    }
    updateChoice(parkKey, { jobs: newJobs });
  };

  /**
   * Make one park the one that receives unallocated transactions, or nobody when parkKey is null.
   */
  const handleUnallocatedChange = (parkKey) => {
    setSaveSummary(null);
    setApiProblems([]);

    // Exactly one park, or none, keeps the flag
    setChoices(previous => {
      const next = {};
      for (const key of Object.keys(previous)) {
        const isChosen = key === parkKey;
        next[key] = { ...previous[key], unallocatedTransactions: isChosen };
      }
      return next;
    });
  };

  // ---------------------------------------------------------------- #
  // Step 3: save
  // ---------------------------------------------------------------- #

  /**
   * Save every park linked to the file; parks left unticked are unlinked.
   */
  const handleSave = async () => {
    // Build one entry per linked park, with every key filled
    const parksToSave = {};
    for (const row of rows) {
      const choice = choices[row.parkKey];
      if (!choice || !choice.linked) {
        continue;
      }

      // Whole company file parks send an empty job list
      let jobs = choice.jobs;
      if (choice.integrationMethod === METHOD_WHOLE) {
        jobs = [];
      }
      parksToSave[row.parkKey] = {
        integrationMethod: choice.integrationMethod,
        jobs: jobs,
        unallocatedTransactions: choice.unallocatedTransactions === true
      };
    }

    setBusy(true);
    setError(null);
    setApiProblems([]);
    try {
      // Save the whole file
      const result = await saveMyobFileMapping(selectedFile.myobId, parksToSave);
      setChanged(true);

      // Count the parks the register would not let us change
      const statusChanges = result.data.statusChanges || [];
      let skippedCount = 0;
      for (const statusChange of statusChanges) {
        if (statusChange.skippedBecause) {
          skippedCount += 1;
        }
      }

      // The current choices are now the saved ones
      const unlinkedParkiIds = result.data.unlinkedParkiIds || [];
      setSavedChoices(choices);
      setSaveSummary({
        linkedCount: Object.keys(parksToSave).length,
        unlinkedCount: unlinkedParkiIds.length,
        skippedCount: skippedCount,
        batchJob: result.data.batchJob || null
      });

      // Read the files again so step 1 shows the new links
      await loadFiles();
    } catch (err) {
      setError(err.message || 'The changes could not be saved.');
      const returnedProblems = err.data?.problems;
      if (Array.isArray(returnedProblems)) {
        setApiProblems(returnedProblems);
      }
    } finally {
      setBusy(false);
    }
  };

  /**
   * After a save, go back to the Map parks step so another file can be picked from its dropdown.
   */
  const handleMapAnother = () => {
    setSaveSummary(null);
    goToStep(STEP_MAP);
  };

  /**
   * Close the wizard, telling the page whether anything was saved.
   */
  const handleClose = () => {
    onClose(changed);
  };

  // ---------------------------------------------------------------- #
  // Render: shared pieces
  // ---------------------------------------------------------------- #

  /**
   * A centred spinner with optional text.
   */
  const renderSpinner = (text = null) => {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary mb-2" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        {text && <div>{text}</div>}
      </div>
    );
  };

  /**
   * The names of a file's parks, joined with commas, or a note when there are none.
   */
  const describeFileParks = (file) => {
    const parkNames = [];
    for (const park of file.parks || []) {
      parkNames.push(park.parkName);
    }
    if (parkNames.length === 0) {
      return 'No parks linked yet';
    }
    return parkNames.join(', ');
  };

  // ---------------------------------------------------------------- #
  // Render: step 1
  // ---------------------------------------------------------------- #

  /**
   * Draw the connected company files and the Connect button.
   */
  const renderConnectStep = () => {
    if (loading) {
      return renderSpinner();
    }

    // Staff viewing as the customer see a locked button; connecting is the customer's job
    let connectLabel = 'Connect a MYOB company file';
    if (files.length > 0) {
      connectLabel = 'Connect another company file';
    }
    let connectButton = (
      <button type="button" className="btn btn-primary" onClick={handleConnect} disabled={busy}>
        <i className="icon-base ti tabler-plug-connected me-1"></i>
        {connectLabel}
      </button>
    );
    if (!canConnect) {
      connectButton = (
        <button type="button" className="btn btn-label-secondary" disabled title={IMPERSONATING_MESSAGE}>
          <i className="icon-base ti tabler-lock me-1"></i>
          Customer connects this
        </button>
      );
    }

    return (
      <>
        {/* Not connected: one heading, one line, one button */}
        {files.length === 0 && (
          <div className="integrations-connect-hero">
            <h4>Connect MYOB</h4>
            <p>Pick one company file on MYOB's screen. Connect again for each other file.</p>
            {connectButton}
          </div>
        )}

        {/* Connected: one compact card per company file, then the button to add another */}
        {files.length > 0 && (
          <>
            {files.map(file => (
              <div key={file.myobId} className="integrations-connection-card mb-3">
                <ConnectionAvatar name={file.companyName} />
                <div className="integrations-connection-card-text">
                  <div className="fw-medium">{file.companyName}</div>
                  <div className="text-body-secondary small">{describeFileParks(file)}</div>
                  {file.otherParkCount > 0 && (
                    <div className="text-body-secondary small">Also linked to {file.otherParkCount} park(s) outside your park list.</div>
                  )}
                </div>
                <button type="button" className="btn btn-sm btn-label-primary" onClick={() => handleMapFile(file)} disabled={busy}>
                  Map parks
                </button>
              </div>
            ))}
            <div className="text-center">
              {connectButton}
            </div>
          </>
        )}
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: step 2, the file at the top
  // ---------------------------------------------------------------- #

  /**
   * Draw the top of the Map parks step: which company file is being mapped (a dropdown when
   * there are several), and whether MYOB is still importing its jobs.
   */
  const renderFileBar = () => {
    // Work out the import state of the file being mapped: running (it has parks), or not started (no park saved yet)
    const jobsMissing = Boolean(jobList) && jobList.jobsImported !== true;
    const stillImporting = jobsMissing && fileHasParks;
    const notImportedYet = jobsMissing && !fileHasParks;
    const firstLoad = jobsLoading && !jobList;
    const hasSeveralFiles = files.length > 1;
    const showPicker = hasSeveralFiles || !selectedFile;

    // The dropdown's choices: one per file, with a prompt when none is picked yet
    const fileOptions = [];
    for (const file of files) {
      fileOptions.push(
        <option key={file.myobId} value={String(file.myobId)}>{file.companyName}</option>
      );
    }
    let pickedValue = '';
    if (selectedFile) {
      pickedValue = String(selectedFile.myobId);
    }

    return (
      <>
        {/* Several files, or none picked yet: say which one, with a dropdown to switch */}
        {showPicker && (
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <label htmlFor="myob-file-picker" className="mb-0">Parks that use</label>
            <select
              id="myob-file-picker"
              className="form-select form-select-sm w-auto fw-medium"
              value={pickedValue}
              onChange={(event) => handlePickFile(event.target.value)}
              disabled={busy}
            >
              {!selectedFile && <option value="">Pick a company file</option>}
              {fileOptions}
            </select>
          </div>
        )}

        {/* One file: name it in the sentence */}
        {!showPicker && (
          <p>Parks that use <strong>{fileName}</strong></p>
        )}

        {/* Still reading the file's jobs */}
        {selectedFile && firstLoad && (
          <p className="text-body-secondary small">
            <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
            Reading jobs...
          </p>
        )}

        {/* No park saved to this file yet, so nothing is importing: say how to get the jobs */}
        {selectedFile && notImportedYet && (
          <div className="alert alert-secondary" role="status">{NOT_IMPORTED_MESSAGE}</div>
        )}

        {/* MYOB has not finished importing them yet */}
        {selectedFile && stillImporting && (
          <div className="alert alert-info d-flex flex-wrap align-items-center justify-content-between gap-2" role="status">
            <span>
              <span className="spinner-border spinner-border-sm me-2" aria-hidden="true"></span>
              {IMPORTING_MESSAGE} You can link a park to the whole company file now.
            </span>
            <button type="button" className="btn btn-sm btn-label-primary" onClick={handleCheckImport} disabled={jobsLoading}>
              Check again
            </button>
          </div>
        )}
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: step 2, the park list and the park's settings
  // ---------------------------------------------------------------- #

  /**
   * Work out the note, problems and lock state shared by a park's list item and its settings panel.
   */
  const describeParkRow = (row) => {
    const choice = choices[row.parkKey] || buildEmptyChoice();

    // A park on Xero cannot be linked here, unless it already is (then the rule problem says so)
    const blockedByXero = row.xeroNames.length > 0 && !choice.linked;
    const linkDisabled = row.locked || blockedByXero;

    // Pick the note shown about the park
    let parkNote = null;
    if (row.locked) {
      parkNote = row.lockedReason;
    } else if (blockedByXero) {
      parkNote = `Set up with Xero (${row.xeroNames.join(', ')})`;
    }

    return {
      choice: choice,
      linkDisabled: linkDisabled,
      parkNote: parkNote,
      problems: problemsForPark(row.parkiId)
    };
  };

  /**
   * Show the tooltip above a locked pill. Pills that are free to pick have no tooltip.
   */
  const showPillTip = (event, text) => {
    if (!text) {
      return;
    }
    const pillBox = event.currentTarget.getBoundingClientRect();
    const left = pillBox.left + pillBox.width / 2;
    const top = pillBox.top - PILL_TOOLTIP_GAP;
    setPillTip({ text: text, left: left, top: top });
  };

  /**
   * Hide the pill tooltip.
   */
  const hidePillTip = () => {
    setPillTip(null);
  };

  /**
   * The name of another linked park that already uses this job, or null when none does.
   */
  const findOtherParkUsingJob = (parkKey, jobId) => {
    for (const otherRow of rows) {
      const isThisPark = otherRow.parkKey === parkKey;
      if (isThisPark) {
        continue;
      }
      const otherChoice = choices[otherRow.parkKey];
      const otherIsLinked = Boolean(otherChoice) && otherChoice.linked === true;
      if (!otherIsLinked) {
        continue;
      }

      // A whole-file park does not use jobs, even if old settings still list some
      const otherUsesSplit = otherChoice.integrationMethod === METHOD_JOBS;
      if (!otherUsesSplit) {
        continue;
      }
      const otherUsesJob = otherChoice.jobs.includes(jobId);
      if (otherUsesJob) {
        return otherRow.parkName;
      }
    }
    return null;
  };

  /**
   * Draw the file's jobs as clickable pills; a pill is on when the park uses that job.
   * A job another park already uses is disabled here until that park lets it go.
   */
  const renderJobPills = (row) => {
    const choice = choices[row.parkKey];
    const jobs = jobList?.jobs || [];

    // Nothing to pick yet
    if (jobs.length === 0) {
      return <span className="text-body-secondary small">No jobs imported</span>;
    }

    return (
      <div className="integrations-pills" role="group" aria-label="Jobs">
        {jobs.map(job => {
          const jobId = String(job.jobId);
          const isOn = choice.jobs.includes(jobId);

          // Another park holds this job, so it is locked here (unless this park also has it, so it can be let go)
          let takenBy = null;
          if (!isOn) {
            takenBy = findOtherParkUsingJob(row.parkKey, jobId);
          }
          const isTaken = takenBy !== null;

          // The pill's look and its hover text
          let pillClass = 'integrations-pill';
          if (isOn) {
            pillClass += ' is-on';
          }
          if (isTaken) {
            pillClass += ' is-taken';
          }
          let hoverText = undefined;
          if (isTaken) {
            hoverText = `Used by ${takenBy}`;
          }

          return (
            <button
              key={jobId}
              type="button"
              className={pillClass}
              aria-pressed={isOn}
              aria-disabled={isTaken}
              onMouseEnter={(event) => showPillTip(event, hoverText)}
              onMouseLeave={hidePillTip}
              onFocus={(event) => showPillTip(event, hoverText)}
              onBlur={hidePillTip}
              onClick={() => {
                if (isTaken) {
                  return;
                }
                handleJobTick(row.parkKey, jobId, !isOn);
              }}
            >
              {isOn && <i className="icon-base ti tabler-check" aria-hidden="true"></i>}
              {job.jobName}
              {isTaken && <span className="visually-hidden">, used by {takenBy}</span>}
            </button>
          );
        })}
      </div>
    );
  };

  /**
   * Draw one park in the left-hand list: an icon for its state, its name and a one-line summary.
   */
  const renderParkListItem = (row, isActive) => {
    const details = describeParkRow(row);
    const isOpenedPark = parkiId !== null && String(row.parkiId) === String(parkiId);
    const hasProblems = details.problems.length > 0;

    // The summary line: its settings when linked, else why it cannot be linked, else nothing
    let summary = '';
    if (details.choice.linked) {
      summary = describeSettings(details.choice, jobList);
    } else if (details.parkNote) {
      summary = details.parkNote;
    }

    // The icon: a warning when a rule is broken, a tick when linked, a dashed circle otherwise
    let iconClass = 'tabler-circle-dashed text-body-secondary';
    if (details.choice.linked) {
      iconClass = 'tabler-circle-check text-success';
    }
    if (hasProblems) {
      iconClass = 'tabler-alert-triangle text-danger';
    }

    // The selected park is highlighted
    let itemClass = 'integrations-mapper-item';
    if (isActive) {
      itemClass += ' is-active';
    }
    if (!details.choice.linked) {
      itemClass += ' is-unlinked';
    }

    // The park the wizard was opened from is scrolled into view
    let itemRef = null;
    if (isOpenedPark) {
      itemRef = openedParkRow;
    }

    return (
      <button
        key={row.parkKey}
        ref={itemRef}
        type="button"
        className={itemClass}
        aria-current={isActive}
        onClick={() => setActiveParkKey(row.parkKey)}
      >
        <i className={`icon-base ti ${iconClass} integrations-mapper-item-icon`} aria-hidden="true"></i>
        <span className="integrations-mapper-item-text">
          <span className="integrations-mapper-item-name">{row.parkName}</span>
          {summary !== '' && (
            <span className="integrations-mapper-item-summary">{summary}</span>
          )}
        </span>
      </button>
    );
  };

  /**
   * Draw the right-hand panel: the selected park's link switch and, when linked, its settings.
   */
  const renderParkPanel = (row) => {
    const details = describeParkRow(row);
    const choice = details.choice;
    const usesJobs = choice.integrationMethod === METHOD_JOBS;
    const switchId = `myob-linked-${row.parkKey}`;

    // Which other park, if any, gets the transactions with no job now
    let otherUnallocatedName = null;
    for (const otherRow of rows) {
      const isThisPark = otherRow.parkKey === row.parkKey;
      const otherHasFlag = choices[otherRow.parkKey]?.unallocatedTransactions === true;
      if (!isThisPark && otherHasFlag) {
        otherUnallocatedName = otherRow.parkName;
      }
    }

    // Locked parks and unlinked parks have no settings to edit
    const showSettings = choice.linked && !row.locked;
    const showLockedSettings = choice.linked && row.locked;

    // Only one park per file can get the transactions with no job: when another park has it, this tick is locked
    const thisParkHasUnallocated = choice.unallocatedTransactions === true;
    const unallocatedTakenElsewhere = otherUnallocatedName !== null && !thisParkHasUnallocated;
    let unallocatedBoxClass = 'form-check integrations-mapper-check mb-0';
    if (unallocatedTakenElsewhere) {
      unallocatedBoxClass += ' is-disabled';
    }

    // The method buttons: the chosen one is filled, the other outlined
    let wholeButtonClass = 'btn btn-sm btn-secondary';
    let jobsButtonClass = 'btn btn-sm btn-outline-secondary';
    if (usesJobs) {
      wholeButtonClass = 'btn btn-sm btn-outline-secondary';
      jobsButtonClass = 'btn btn-sm btn-secondary';
    }

    /**
     * Give this park the transactions with no job, or take them away so no park gets them.
     */
    const handleUnallocatedTick = (ticked) => {
      if (ticked) {
        handleUnallocatedChange(row.parkKey);
        return;
      }
      handleUnallocatedChange(null);
    };

    return (
      <div className="integrations-mapper-panel">
        {/* The park's name and its link switch */}
        <div className="integrations-mapper-panel-head">
          <h5 className="mb-0">{row.parkName}</h5>
          <div className="form-check form-switch mb-0">
            <input
              id={switchId}
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={choice.linked}
              disabled={details.linkDisabled}
              onChange={(event) => handleLinkedChange(row.parkKey, event.target.checked)}
            />
            <label className="form-check-label" htmlFor={switchId}>Uses this file</label>
          </div>
        </div>

        {/* Why the park cannot be changed here */}
        {details.parkNote && (
          <p className="text-body-secondary small mb-0">{details.parkNote}</p>
        )}

        {/* Rule problems for this park */}
        {details.problems.length > 0 && (
          <div className="alert alert-danger mb-0" role="alert">
            {details.problems.map((message, index) => (
              <div key={index}>{message}</div>
            ))}
          </div>
        )}

        {/* Not linked: one line saying what the switch does */}
        {!choice.linked && !details.linkDisabled && (
          <p className="text-body-secondary mb-0">This park does not use {fileName}. Switch on "Uses this file" to choose its transactions.</p>
        )}

        {/* Locked but linked: show the settings as text */}
        {showLockedSettings && (
          <p className="text-body-secondary mb-0">{describeSettings(choice, jobList)}</p>
        )}

        {/* Linked: whole file or by job */}
        {showSettings && (
          <div className="integrations-mapper-field">
            <span className="integrations-mapper-label" id={`myob-method-${row.parkKey}`}>Which transactions</span>
            <div className="btn-group" role="group" aria-labelledby={`myob-method-${row.parkKey}`}>
              <button
                type="button"
                className={wholeButtonClass}
                aria-pressed={!usesJobs}
                onClick={() => handleMethodChange(row.parkKey, METHOD_WHOLE)}
              >
                {METHOD_LABELS[METHOD_WHOLE]}
              </button>
              <button
                type="button"
                className={jobsButtonClass}
                aria-pressed={usesJobs}
                onClick={() => handleMethodChange(row.parkKey, METHOD_JOBS)}
              >
                By job
              </button>
            </div>
          </div>
        )}

        {/* Linked by job: the jobs this park uses */}
        {showSettings && usesJobs && (
          <div className="integrations-mapper-field">
            <span className="integrations-mapper-label">Jobs for this park</span>
            {renderJobPills(row)}
          </div>
        )}

        {/* Linked by job: whether it also gets transactions with no job */}
        {showSettings && usesJobs && (
          <div className={unallocatedBoxClass}>
            <input
              id={`myob-unallocated-${row.parkKey}`}
              type="checkbox"
              className="form-check-input"
              checked={choice.unallocatedTransactions === true}
              disabled={unallocatedTakenElsewhere}
              onChange={(event) => handleUnallocatedTick(event.target.checked)}
            />
            <label className="form-check-label" htmlFor={`myob-unallocated-${row.parkKey}`}>
              Also gets transactions with no job
              <span className="text-body-secondary">
                {unallocatedTakenElsewhere && ` (set for ${otherUnallocatedName}. Untick it there first.)`}
                {!unallocatedTakenElsewhere && ' (one park per file)'}
              </span>
            </label>
          </div>
        )}
      </div>
    );
  };

  /**
   * Draw the Map parks step: the file at the top, then the park list on the left and the selected park's settings on the right.
   */
  const renderMapStep = () => {
    if (loading) {
      return renderSpinner();
    }
    if (files.length === 0) {
      return <p className="mb-0">Connect a MYOB company file first.</p>;
    }

    // No file picked yet: show only the dropdown
    if (!selectedFile) {
      return renderFileBar();
    }

    // Keep the parks whose name matches the search, split into linked and not linked
    const filterText = parkFilter.trim().toLowerCase();
    const linkedRows = [];
    const unlinkedRows = [];
    for (const row of rows) {
      const parkName = (row.parkName || '').toLowerCase();
      const matches = filterText === '' || parkName.includes(filterText);
      if (!matches) {
        continue;
      }
      const isLinked = choices[row.parkKey]?.linked === true;
      if (isLinked) {
        linkedRows.push(row);
      } else {
        unlinkedRows.push(row);
      }
    }

    // The selected park: the one clicked, else the opened park, else the first linked park, else the first park
    let activeRow = null;
    for (const row of rows) {
      if (row.parkKey === activeParkKey) {
        activeRow = row;
      }
    }
    if (!activeRow) {
      for (const row of rows) {
        const isOpenedPark = parkiId !== null && String(row.parkiId) === String(parkiId);
        if (isOpenedPark) {
          activeRow = row;
        }
      }
    }
    if (!activeRow && linkedRows.length > 0) {
      activeRow = linkedRows[0];
    }
    if (!activeRow && rows.length > 0) {
      activeRow = rows[0];
    }
    const activeKey = activeRow ? activeRow.parkKey : null;

    return (
      <>
        {renderFileBar()}

        <div className="integrations-mapper">
          {/* Left: search, then the linked parks, then the rest */}
          <nav className="integrations-mapper-list" aria-label="Parks">
            <div className="integrations-mapper-search">
              <input
                type="search"
                className="form-control form-control-sm"
                placeholder="Search parks"
                aria-label="Search parks"
                value={parkFilter}
                onChange={(event) => setParkFilter(event.target.value)}
              />
            </div>
            <div className="integrations-mapper-items">
              <div className="integrations-mapper-group">Linked ({linkedRows.length})</div>
              {linkedRows.map(row => renderParkListItem(row, row.parkKey === activeKey))}
              {linkedRows.length === 0 && (
                <div className="integrations-mapper-empty">No parks use this file yet.</div>
              )}
              <div className="integrations-mapper-group">Not linked ({unlinkedRows.length})</div>
              {unlinkedRows.map(row => renderParkListItem(row, row.parkKey === activeKey))}
            </div>
          </nav>

          {/* Right: the selected park */}
          {activeRow && renderParkPanel(activeRow)}
        </div>
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Render: step 3
  // ---------------------------------------------------------------- #

  /**
   * Draw the list of changes, the rule problems, and the result once saved.
   */
  const renderReviewStep = () => {
    // After saving, say what happened
    if (saveSummary) {
      let savedText = `Saved ${fileName}: ${saveSummary.linkedCount} park(s) linked.`;
      if (saveSummary.unlinkedCount > 0) {
        savedText = `Saved ${fileName}: ${saveSummary.linkedCount} park(s) linked, ${saveSummary.unlinkedCount} unlinked.`;
      }
      const batchJobText = BATCH_JOB_MESSAGES[saveSummary.batchJob] || null;
      return (
        <>
          <div className="alert alert-success" role="status">{savedText}</div>
          {batchJobText && (
            <p>{batchJobText}</p>
          )}
          {saveSummary.skippedCount > 0 && (
            <p className="text-body-secondary">{saveSummary.skippedCount} park(s) did not have their register status changed because they are not in your plan or need Parki support.</p>
          )}
        </>
      );
    }

    // List the changes park by park
    const changes = buildChanges();

    // Problems from the form, else the ones the API sent back
    let shownProblems = ruleProblems;
    if (shownProblems.length === 0) {
      shownProblems = apiProblems;
    }

    return (
      <>
        {changes.length === 0 && (
          <p>No changes to save.</p>
        )}
        {changes.length > 0 && (
          <>
            <p>These changes will be saved to <strong>{fileName}</strong>:</p>
            <ul className="integrations-review-list">
              {changes.map(change => (
                <li key={change.row.parkKey}>
                  <strong>{change.row.parkName}</strong>
                  <ul>
                    {change.kind === 'linked' && (
                      <li>Linked: {describeSettings(change.after, jobList)}</li>
                    )}
                    {change.kind === 'unlinked' && (
                      <li>Unlinked. It stops receiving data from {fileName}.</li>
                    )}
                    {change.kind === 'changed' && (
                      <>
                        <li>Was: {describeSettings(change.before, jobList)}</li>
                        <li>Now: {describeSettings(change.after, jobList)}</li>
                      </>
                    )}
                  </ul>
                </li>
              ))}
            </ul>
          </>
        )}

        {/* Rule problems, which block the save */}
        {shownProblems.length > 0 && (
          <div className="alert alert-danger" role="alert">
            <div className="fw-medium mb-1">Fix these on the Map parks step before saving:</div>
            <ul className="mb-0">
              {shownProblems.map((problem, index) => (
                <li key={index}>{problem.message}</li>
              ))}
            </ul>
          </div>
        )}
      </>
    );
  };

  // ---------------------------------------------------------------- #
  // Footer button for each step
  // ---------------------------------------------------------------- #

  // Next by default; Review from the map; Save on Review; Close once saved
  let primaryLabel = 'Next';
  let onPrimary = () => goToStep(step + 1);
  let primaryDisabled = false;
  let footerStart = null;
  if (step === STEP_CONNECT) {
    primaryDisabled = loading || files.length === 0;
  } else if (step === STEP_MAP) {
    primaryLabel = 'Review';
    primaryDisabled = !selectedFile;
  } else if (step === STEP_REVIEW) {
    if (saveSummary) {
      primaryLabel = 'Close';
      onPrimary = handleClose;
      footerStart = (
        <button type="button" className="btn btn-sm btn-label-secondary" onClick={handleMapAnother}>
          Map another company file
        </button>
      );
    } else {
      const noChanges = buildChanges().length === 0;
      const hasProblems = ruleProblems.length > 0;
      primaryLabel = 'Save';
      onPrimary = handleSave;
      primaryDisabled = noChanges || hasProblems || !selectedFile;
    }
  }

  // Unsaved choices make closing ask first
  const dirty = isDirty();

  // ---------------------------------------------------------------- #
  // Render
  // ---------------------------------------------------------------- #

  return (
    <WizardFrame
      title="MYOB"
      logoSrc={`${INTEGRATIONS_LOGO_PATH}/myob.png`}
      register={register}
      parkiId={parkiId}
      products={['bookkeeping']}
      onNotNeededChange={() => setChanged(true)}
      steps={MYOB_STEPS}
      step={step}
      maxReachedStep={maxReachedStep}
      onStepChange={goToStep}
      error={error}
      dirty={dirty}
      onClose={handleClose}
      primaryLabel={primaryLabel}
      onPrimary={onPrimary}
      primaryDisabled={primaryDisabled}
      busy={busy}
      hideBack={Boolean(saveSummary)}
      footerStart={footerStart}
    >
      {step === STEP_CONNECT && renderConnectStep()}
      {step === STEP_MAP && renderMapStep()}
      {step === STEP_REVIEW && renderReviewStep()}

      {/* The tooltip over a locked pill, drawn on the page body: the modal's transform would otherwise offset a fixed position */}
      {pillTip && createPortal(
        <div className="integrations-tooltip" role="tooltip" style={{ left: pillTip.left, top: pillTip.top }}>
          <div className="integrations-tooltip-title">{pillTip.text}</div>
        </div>,
        document.body
      )}
    </WizardFrame>
  );
};

export default MyobWizard;
